import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { fetchComplaintCase, updateComplaintReview, fetchComplaintHistory, fetchComplaintsOptions, getTaskAudioUrl } from '../api';
import { toast } from '../hooks/use-toast';
import { STATUS_ORDER, STATUS_LABELS } from './complaints/status';
import TransferredPointWarning from './complaints/TransferredPointWarning';

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateTimeFormatter.format(date);
};

const formatCaseDate = (value) => {
  if (!value) return '—';
  const parts = String(value).split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value;
};

const resolveRecordingUrl = (call) => {
  if (!call) return null;
  const trimmed = call.recordingUrl?.trim();
  // Относительный /api/tasks/.../audio не несёт токен/базовый адрес API — берём через getTaskAudioUrl.
  if (trimmed?.startsWith('http')) return trimmed;
  return getTaskAudioUrl(call.callId);
};

const HISTORY_FIELD_LABELS = {
  status: 'Статус',
  type: 'Тип жалобы',
  detail: 'Детализация',
  compensation: 'Компенсация',
  comment: 'Комментарий',
  assignee: 'Ответственный'
};

// Radix Select не допускает пустую строку как value — используем сентинел для «не указано».
const NONE_VALUE = '__none__';

const mergeOptions = (systemValues, caseValues, current) => {
  const set = new Set();
  (systemValues ?? []).forEach((v) => { if (v) set.add(v); });
  (caseValues ?? []).forEach((v) => { if (v) set.add(v); });
  if (current) set.add(current);
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
};

const ComplaintCaseModal = ({ caseKey, onClose, onSaved }) => {
  const open = Boolean(caseKey);
  const phone = caseKey?.phone;
  const date = caseKey?.date;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCalls, setExpandedCalls] = useState(() => new Set());
  const [history, setHistory] = useState([]);
  const [options, setOptions] = useState({ types: [], details: [] });

  const [form, setForm] = useState({ status: 'new', type: '', detail: '', compensation: '', comment: '' });

  // Все допустимые значения типа/детализации в системе — для выпадающих списков (грузим один раз).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchComplaintsOptions();
        if (!cancelled) {
          setOptions({ types: data?.types ?? [], details: data?.details ?? [] });
        }
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadDetail = useCallback(async () => {
    if (!phone || !date) return;
    setLoading(true);
    try {
      const data = await fetchComplaintCase(phone, date);
      setDetail(data);
      const c = data?.case ?? {};
      setForm({
        status: c.status ?? 'new',
        type: c.type ?? '',
        detail: c.detail ?? '',
        compensation: c.compensation ?? '',
        comment: c.comment ?? ''
      });
    } catch (err) {
      console.error(err);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [phone, date]);

  const loadHistory = useCallback(async () => {
    if (!phone || !date) return;
    try {
      const data = await fetchComplaintHistory(phone, date);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setHistory([]);
    }
  }, [phone, date]);

  useEffect(() => {
    if (!open) return;
    setExpandedCalls(new Set());
    loadDetail();
    loadHistory();
  }, [open, loadDetail, loadHistory]);

  const toggleCall = useCallback((callId) => {
    setExpandedCalls((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId); else next.add(callId);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!phone || !date) return;
    setSaving(true);
    try {
      // PUT возвращает обновлённый кейс — обновляем состояние точечно, без полной перезагрузки
      // (иначе мигает «Загрузка кейса…» и заново тянутся звонки/заказ).
      const updated = await updateComplaintReview(phone, date, {
        status: form.status,
        type: form.type || null,
        detail: form.detail || null,
        compensation: form.compensation || null,
        comment: form.comment || null
      });
      toast({ title: 'Разбор сохранён' });
      if (updated) {
        setDetail((prev) => (prev ? { ...prev, case: updated } : prev));
        setForm({
          status: updated.status ?? 'new',
          type: updated.type ?? '',
          detail: updated.detail ?? '',
          compensation: updated.compensation ?? '',
          comment: updated.comment ?? ''
        });
      }
      await loadHistory();
      onSaved?.();
    } catch (err) {
      console.error(err);
      toast({
        title: 'Не удалось сохранить разбор',
        description: err?.response?.data?.message ?? err.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  }, [phone, date, form, loadHistory, onSaved]);

  const c = detail?.case;
  const calls = detail?.calls ?? [];
  const orders = detail?.orders ?? [];

  const typeChanged = useMemo(
    () => c && c.modelType && c.type && c.modelType !== c.type,
    [c]
  );
  const detailChanged = useMemo(
    () => c && c.modelDetail && c.detail && c.modelDetail !== c.detail,
    [c]
  );

  // Список значений = справочник системы + категории текущего кейса + текущее значение формы.
  const typeOptionList = useMemo(
    () => mergeOptions(options.types, c?.categories, form.type),
    [options.types, c?.categories, form.type]
  );
  const detailOptionList = useMemo(
    () => mergeOptions(options.details, c?.subCategories, form.detail),
    [options.details, c?.subCategories, form.detail]
  );

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="flex h-[58vh] max-w-6xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            Жалоба · {c?.clientName || phone} · {formatCaseDate(date)}
          </DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1">
              {phone} · звонков в кейсе: {c?.callsCount ?? calls.length}
              {c?.point && (
                <>
                  {' '}· точка: {c.point}
                  {c.pointTransferred && <TransferredPointWarning />}
                </>
              )}
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="px-1 py-6 text-sm text-slate-500">Загрузка кейса...</div>
        ) : !detail ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Кейс не найден.
          </div>
        ) : (
          <Tabs defaultValue="review" className="flex min-h-0 w-full flex-1 flex-col">
            <TabsList className="shrink-0">
              <TabsTrigger value="review">Разбор</TabsTrigger>
              <TabsTrigger value="calls">Звонки ({calls.length})</TabsTrigger>
              <TabsTrigger value="order">Заказ ({orders.length})</TabsTrigger>
              <TabsTrigger value="history">История</TabsTrigger>
            </TabsList>

            <TabsContent value="review" className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Статус">
                    <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((status) => (
                          <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Компенсация">
                    <Input
                      value={form.compensation}
                      placeholder="Например: бонус 500 ₽"
                      onChange={(e) => setForm((f) => ({ ...f, compensation: e.target.value }))}
                    />
                  </Field>
                  <Field label={`Тип жалобы${typeChanged ? ` (модель: ${c.modelType})` : ''}`}>
                    <Select
                      value={form.type || NONE_VALUE}
                      onValueChange={(v) => setForm((f) => ({ ...f, type: v === NONE_VALUE ? '' : v }))}
                    >
                      <SelectTrigger><SelectValue placeholder="Не указано" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>— не указано —</SelectItem>
                        {typeOptionList.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={`Детализация${detailChanged ? ` (модель: ${c.modelDetail})` : ''}`}>
                    <Select
                      value={form.detail || NONE_VALUE}
                      onValueChange={(v) => setForm((f) => ({ ...f, detail: v === NONE_VALUE ? '' : v }))}
                    >
                      <SelectTrigger><SelectValue placeholder="Не указано" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>— не указано —</SelectItem>
                        {detailOptionList.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Комментарий к разбору">
                  <Textarea
                    value={form.comment}
                    rows={3}
                    onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                  />
                </Field>
                {c?.summary && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <div className="mb-1 font-medium text-slate-900">Summary</div>
                    {c.summary}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    Сотрудники: {(c?.employees ?? []).join(', ') || '—'}
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="gap-1">
                    <Save className="h-4 w-4" /> {saving ? 'Сохраняем...' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="calls" className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
              <CallsList calls={calls} expandedCalls={expandedCalls} onToggle={toggleCall} />
            </TabsContent>

            <TabsContent value="order" className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
              <OrdersList orders={orders} point={c?.point} pointTransferred={c?.pointTransferred} />
            </TabsContent>

            <TabsContent value="history" className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1">
              <HistoryList history={history} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-slate-500">{label}</div>
    {children}
  </div>
);

const CallsList = ({ calls, expandedCalls, onToggle }) => {
  if (calls.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Нет звонков в кейсе.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Время</TableHead>
            <TableHead>Оператор</TableHead>
            <TableHead>Жалоба</TableHead>
            <TableHead>Детализация</TableHead>
            <TableHead>Причина</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.map((call) => {
            const isExpanded = expandedCalls.has(call.callId);
            const Icon = isExpanded ? ChevronDown : ChevronRight;
            return (
              <React.Fragment key={call.callId}>
                <TableRow className="cursor-pointer" onClick={() => onToggle(call.callId)}>
                  <TableCell><Icon className="h-4 w-4 text-slate-500" /></TableCell>
                  <TableCell className="whitespace-nowrap">{formatDateTime(call.callStartedAt)}</TableCell>
                  <TableCell>{call.employeeName || '—'}</TableCell>
                  <TableCell>
                    {(call.negativeCategories?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {call.negativeCategories.map((category, idx) => (
                          <Badge key={idx} variant="outline" className="border-red-200 bg-red-50 text-xs text-red-700">{category}</Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">
                        {call.callClassificationType === 'delivery_issue' ? 'Подозрение' : '—'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(call.negativeSubCategories?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {call.negativeSubCategories.map((sub, idx) => (
                          <Badge key={idx} variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-700">{sub}</Badge>
                        ))}
                      </div>
                    ) : <span className="text-sm text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="min-w-[16rem] max-w-md align-top text-xs italic text-slate-600">
                    {call.negativeReason || <span className="not-italic text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-slate-50/70 p-0">
                      <div className="space-y-4 px-4 py-4">
                        <audio className="w-full" src={resolveRecordingUrl(call)} controls />
                        <div className="rounded-lg border border-slate-200 bg-white p-4">
                          <div className="mb-2 text-sm font-semibold text-slate-900">Транскрипция</div>
                          {call.transcript ? (
                            <pre className="whitespace-pre-wrap break-words text-sm text-slate-700">{call.transcript}</pre>
                          ) : (
                            <div className="text-sm text-slate-500">Транскрипция недоступна.</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const HistoryList = ({ history }) => {
  if (history.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Правок ещё не было.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {history.map((entry, idx) => (
        <div key={idx} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-900">{HISTORY_FIELD_LABELS[entry.field] ?? entry.field}</span>
            <span className="text-xs text-slate-400">{formatDateTime(entry.changedAtUtc)}</span>
          </div>
          <div className="mt-1 text-slate-600">
            <span className="text-slate-400 line-through">{entry.oldValue || '—'}</span>
            {' → '}
            <span className="text-slate-900">{entry.newValue || '—'}</span>
          </div>
          {entry.changedByUsername && (
            <div className="mt-1 text-xs text-slate-400">Автор: {entry.changedByUsername}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  return `${number.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽`;
};

const OrdersList = ({ orders, point, pointTransferred }) => {
  if (!orders || orders.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        Заказ за эту дату не найден в кэше iiko. Откройте кейс ещё раз или прогрейте точки кнопкой «Обновить точки из iiko».
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {point && (
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <span className="font-medium text-slate-900">Точка:</span> {point}
          {pointTransferred && <TransferredPointWarning />}
        </div>
      )}
      {orders.map((order) => (
        <OrderCard key={order.orderNumber} order={order} />
      ))}
    </div>
  );
};

const OrderCard = ({ order }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
    <div className="flex items-center justify-between">
      <div className="font-medium text-slate-900">
        Заказ #{order.orderNumber}
        {order.status ? ` · ${order.status}` : ''}
      </div>
      <div className="font-semibold text-slate-900">{formatMoney(order.total)}</div>
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      <div className="text-slate-500">Время</div>
      <div>{formatDateTime(order.orderDate)}</div>
      {order.conception && (
        <>
          <div className="text-slate-500">Точка</div>
          <div>{order.conception}</div>
        </>
      )}
      <div className="text-slate-500">Адрес</div>
      <div>{order.address || '—'}</div>
      {order.comment && (
        <>
          <div className="text-slate-500">Комментарий</div>
          <div className="italic">{order.comment}</div>
        </>
      )}
      {order.cancelCauseName && (
        <>
          <div className="text-red-600">Причина отмены</div>
          <div className="text-red-700">{order.cancelCauseName}</div>
        </>
      )}
    </div>
    {Array.isArray(order.items) && order.items.length > 0 && (
      <div className="mt-1">
        <div className="mb-1 font-medium text-slate-800">Состав</div>
        <div className="space-y-1">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="flex-1">
                {item.name}
                {item.amount !== 1 && ` × ${item.amount}`}
                {item.comment && <span className="ml-1 italic text-slate-400">({item.comment})</span>}
              </span>
              <span className="shrink-0 text-slate-500">{item.price} ₽</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default ComplaintCaseModal;
